
import React from 'react';
import { Link } from 'react-router-dom';
import {  RESOURCES_DATA,  ADMIN_NAVIGATION_LINKS } from '../../constants';
import { BookOpenIcon, DocumentDuplicateIcon, UsersIcon, CogIcon, type IconProps } from '../../components/icons'; // Ensure UsersIcon is the group icon
import { useTheme } from '../../contexts/ThemeContext';

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactElement<IconProps>; // Changed from React.ReactNode
  linkTo?: string;
  colorClass: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, linkTo, colorClass }) => {
  const { colors } = useTheme();
  const content = (
    <>
      <div 
        className="p-3 rounded-full mb-4 self-start"
        style={{ 
          background: `linear-gradient(135deg, ${colors.primary.main}20 0%, ${colors.accent.mint}20 100%)`,
        }}
      >
        {React.cloneElement(icon, { className: 'w-8 h-8', style: { color: colors.accent.mint } })}
      </div>
      <div>
        <p className="text-3xl font-bold" style={{ color: colors.text.primary }}>{value}</p>
        <p style={{ color: colors.text.secondary }}>{title}</p>
      </div>
    </>
  );

  if (linkTo) {
    return (
      <Link 
        to={linkTo} 
        className="p-6 rounded-lg shadow-xl flex flex-col items-start hover:shadow-md hover:scale-105 transition-all duration-300"
        style={{
          backgroundColor: colors.background.paper,
          borderLeft: `4px solid ${colors.accent.mint}`,
          boxShadow: `0 10px 30px -10px ${colors.accent.mint}20`
        }}
      >
        {content}
      </Link>
    );
  }
  return (
    <div 
      className="p-6 rounded-lg shadow-xl flex flex-col items-start"
      style={{
        backgroundColor: colors.background.paper,
        borderLeft: `4px solid ${colors.accent.mint}`,
        boxShadow: `0 10px 30px -10px ${colors.accent.mint}20`
      }}
    >
      {content}
    </div>
  );
};


const AdminDashboardPage: React.FC = () => {
  const { colors } = useTheme();
  const courseLink = ADMIN_NAVIGATION_LINKS.find(link => link.label === 'Courses');
  const resourceLink = ADMIN_NAVIGATION_LINKS.find(link => link.label === 'Resources');
  const userLink = ADMIN_NAVIGATION_LINKS.find(link => link.label === 'Users');
  const contentLink = ADMIN_NAVIGATION_LINKS.find(link => link.label === 'Site Content');


  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-semibold mb-2" style={{ color: colors.text.primary }}>Welcome, Admin!</h2>
        <p style={{ color: colors.text.secondary }}>Here's an overview of your platform.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Total Courses" 
          value={5} 
          icon={<BookOpenIcon />} 
          linkTo={courseLink?.path}
          colorClass="border-blue-500"
        />
        <StatCard 
          title="Total Resources" 
          value={RESOURCES_DATA.length} 
          icon={<DocumentDuplicateIcon />}
          linkTo={resourceLink?.path}
          colorClass="border-emerald-500"
        />
        <StatCard 
          title="Registered Users" 
          value={6} 
          icon={<UsersIcon />}
          linkTo={userLink?.path}
          colorClass="border-purple-500"
        />
         <StatCard 
          title="Manage Content" 
          value="Edit Site" 
          icon={<CogIcon />}
          linkTo={contentLink?.path}
          colorClass="border-yellow-500"
        />
      </div>

      <div className="p-6 rounded-lg shadow-xl" style={{ backgroundColor: colors.background.paper, boxShadow: `0 10px 30px -10px ${colors.accent.mint}20` }}>
        <h3 className="text-xl font-semibold mb-4" style={{ color: colors.text.primary }}>Quick Actions</h3>
        <div className="flex flex-wrap gap-4">
          {courseLink && (
            <Link 
              to={courseLink.path} 
              className="text-white font-medium py-2 px-4 rounded-lg transition-all duration-300 hover:scale-105"
              style={{
                background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${colors.accent.mint} 0%, ${colors.primary.main} 100%)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`;
              }}
            >
              Manage Courses
            </Link>
          )}
          {resourceLink && (
            <Link 
              to={resourceLink.path} 
              className="text-white font-medium py-2 px-4 rounded-lg transition-all duration-300 hover:scale-105"
              style={{
                background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${colors.accent.mint} 0%, ${colors.primary.main} 100%)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`;
              }}
            >
              Manage Resources
            </Link>
          )}
        </div>
      </div>
      
      <div className="p-6 rounded-lg shadow-xl" style={{ backgroundColor: colors.background.paper, boxShadow: `0 10px 30px -10px ${colors.accent.mint}20` }}>
        <h3 className="text-xl font-semibold mb-4" style={{ color: colors.text.primary }}>System Notes</h3>
        <p style={{ color: colors.text.secondary }}>
          This admin panel is a UI demonstration. For full functionality including payment processing, 
          email notifications, and real-time user visit tracking, a backend system and database integration are required.
        </p>
         <ul className="list-disc list-inside mt-2 space-y-1" style={{ color: colors.text.secondary }}>
            <li>Payment Gateway Integration: Requires backend setup (e.g., Stripe, PayPal).</li>
            <li>Email Functionality: Requires backend integration with an email service (e.g., SendGrid).</li>
            <li>User Visit Tracking: Requires backend analytics and database.</li>
        </ul>
      </div>

    </div>
  );
};

export default AdminDashboardPage;
